import { useMemo } from 'react'
import {
  default as BaseSelect,
  ClassNamesConfig,
  GroupBase,
  Props,
  SelectInstance,
  StylesConfig,
} from 'react-select'
import { AsyncProps, default as BaseAsyncSelect } from 'react-select/async'
import {
  AsyncCreatableProps,
  default as BaseAsyncCreatableSelect,
} from 'react-select/async-creatable'
import { default as BaseCreatableSelect, CreatableProps } from 'react-select/creatable'

import { useFallbackFieldContext } from '@eigtech/ui/components/form'
import { cn } from '@eigtech/ui/lib/utils'

const controlStyles = {
  base: 'flex !min-h-9 w-full rounded-md border border-input bg-transparent pl-3 py-1 pr-1 gap-1 text-sm shadow-xs transition-colors hover:cursor-pointer has-aria-invalid:ring-destructive/50 has-aria-invalid:border-destructive',
  focus: 'outline-none ring-1 ring-ring',
  disabled: 'cursor-not-allowed opacity-50',
}
const placeholderStyles = 'text-sm text-muted-foreground'
const valueContainerStyles = 'gap-1'
const multiValueStyles =
  'inline-flex items-center gap-2 rounded-md border border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-1.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
const indicatorsContainerStyles = 'gap-1'
const clearIndicatorStyles = 'p-1 rounded-md'
const indicatorSeparatorStyles = 'bg-border'
const dropdownIndicatorStyles = 'p-1 rounded-md'
const menuStyles = 'p-1 mt-1 z-80 border bg-popover shadow-md rounded-md text-popover-foreground'
const menuListStyles = 'p-0 m-0 w-full overflow-y-auto'
const menuPortalStyles = 'z-50'
const groupHeadingStyles = 'py-2 px-1 text-secondary-foreground text-sm font-semibold'
const optionStyles = {
  base: 'hover:cursor-pointer hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-sm !text-sm !cursor-default !select-none !outline-none font-sans',
  focus: 'active:bg-accent/90 bg-accent text-accent-foreground',
  disabled: 'pointer-events-none opacity-50',
  selected: 'bg-accent/40',
}
const noOptionsMessageStyles = 'text-muted-foreground py-1.5 text-sm'
const loadingIndicatorStyles = 'flex items-center justify-center h-4 w-4 opacity-50'
const loadingMessageStyles = 'text-accent-foreground p-2 bg-accent'

/**
 * This factory method is used to build custom classNames configuration
 */
function createClassNames<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(classNames: ClassNamesConfig<Option, IsMulti, Group>): ClassNamesConfig<Option, IsMulti, Group> {
  return {
    clearIndicator: (state) => cn(clearIndicatorStyles, classNames?.clearIndicator?.(state)),
    container: (state) => cn(classNames?.container?.(state)),
    control: (state) =>
      cn(
        controlStyles.base,
        state.isDisabled && controlStyles.disabled,
        state.isFocused && controlStyles.focus,
        classNames?.control?.(state)
      ),
    dropdownIndicator: (state) =>
      cn(dropdownIndicatorStyles, classNames?.dropdownIndicator?.(state)),
    group: (state) => cn(classNames?.group?.(state)),
    groupHeading: (state) => cn(groupHeadingStyles, classNames?.groupHeading?.(state)),
    indicatorsContainer: (state) =>
      cn(indicatorsContainerStyles, classNames?.indicatorsContainer?.(state)),
    indicatorSeparator: (state) =>
      cn(indicatorSeparatorStyles, classNames?.indicatorSeparator?.(state)),
    input: (state) => cn(classNames?.input?.(state)),
    loadingIndicator: (state) => cn(loadingIndicatorStyles, classNames?.loadingIndicator?.(state)),
    loadingMessage: (state) => cn(loadingMessageStyles, classNames?.loadingMessage?.(state)),
    menu: (state) => cn(menuStyles, classNames?.menu?.(state)),
    menuList: (state) => cn(menuListStyles, classNames?.menuList?.(state)),
    menuPortal: (state) => cn(menuPortalStyles, classNames?.menuPortal?.(state)),
    multiValue: (state) => cn(multiValueStyles, classNames?.multiValue?.(state)),
    multiValueLabel: (state) => cn(classNames?.multiValueLabel?.(state)),
    multiValueRemove: (state) => cn(classNames?.multiValueRemove?.(state)),
    noOptionsMessage: (state) => cn(noOptionsMessageStyles, classNames?.noOptionsMessage?.(state)),
    option: (state) =>
      cn(
        optionStyles.base,
        state.isFocused && optionStyles.focus,
        state.isDisabled && optionStyles.disabled,
        state.isSelected && optionStyles.selected,
        classNames?.option?.(state)
      ),
    placeholder: (state) => cn(placeholderStyles, classNames?.placeholder?.(state)),
    singleValue: (state) => cn(classNames?.singleValue?.(state)),
    valueContainer: (state) => cn(valueContainerStyles, classNames?.valueContainer?.(state)),
  }
}

function createStyles<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(styles: StylesConfig<Option, IsMulti, Group>): StylesConfig<Option, IsMulti, Group> {
  return {
    ...styles,
    input: (base) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
      ...styles.input,
    }),
    multiValueLabel: (base) => ({
      ...base,
      whiteSpace: 'normal',
      overflow: 'visible',
      ...styles.multiValueLabel,
    }),
    control: (base) => ({
      ...base,
      transition: 'none',
      // minHeight: '2.25rem', // we used !min-h-9 instead
      ...styles.control,
    }),
    menuList: (base) => ({
      ...base,
      '::-webkit-scrollbar': {
        background: 'transparent',
      },
      '::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '::-webkit-scrollbar-thumb': {
        background: 'hsl(var(--border))',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: 'transparent',
      },
      ...styles.menuList,
    }),
  }
}

function Select<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  styles: propsStyles,
  classNames: propsClassNames,
  ...rest
}: Props<Option, IsMulti, Group> & React.RefAttributes<SelectInstance<Option, IsMulti, Group>>) {
  const classNames = useMemo(() => createClassNames(propsClassNames ?? {}), [propsClassNames])
  const styles = useMemo(() => createStyles(propsStyles ?? {}), [propsStyles])

  const context = useFallbackFieldContext()

  return (
    <BaseSelect
      aria-invalid={context?.invalid ?? context?.['aria-invalid']}
      classNames={classNames}
      isDisabled={context?.disabled}
      name={context?.name}
      styles={styles}
      unstyled
      {...rest}
    />
  )
}

function CreatableSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  styles: propsStyles,
  classNames: propsClassNames,
  ...rest
}: CreatableProps<Option, IsMulti, Group> &
  React.RefAttributes<SelectInstance<Option, IsMulti, Group>>) {
  const classNames = useMemo(() => createClassNames(propsClassNames ?? {}), [propsClassNames])
  const styles = useMemo(() => createStyles(propsStyles ?? {}), [propsStyles])

  return <BaseCreatableSelect classNames={classNames} styles={styles} unstyled {...rest} />
}

function AsyncSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  styles: propsStyles,
  classNames: propsClassNames,
  ...rest
}: AsyncProps<Option, IsMulti, Group> &
  React.RefAttributes<SelectInstance<Option, IsMulti, Group>>) {
  const classNames = useMemo(() => createClassNames(propsClassNames ?? {}), [propsClassNames])
  const styles = useMemo(() => createStyles(propsStyles ?? {}), [propsStyles])

  return <BaseAsyncSelect classNames={classNames} styles={styles} unstyled {...rest} />
}

function AsyncCreatableSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  styles: propsStyles,
  classNames: propsClassNames,
  ...rest
}: AsyncCreatableProps<Option, IsMulti, Group> &
  React.RefAttributes<SelectInstance<Option, IsMulti, Group>>) {
  const classNames = useMemo(() => createClassNames(propsClassNames ?? {}), [propsClassNames])
  const styles = useMemo(() => createStyles(propsStyles ?? {}), [propsStyles])

  return <BaseAsyncCreatableSelect classNames={classNames} styles={styles} unstyled {...rest} />
}

// eslint-disable-next-line react-refresh/only-export-components
export * from 'react-select'
export { AsyncCreatableSelect, AsyncSelect, CreatableSelect, Select }
export type { AsyncCreatableProps, AsyncProps, CreatableProps }

// /* ----------- hooks.jsx ----------- */
// /**
// * This hook could be added to your select component if needed:
// *   const formatters = useFormatters()
// *   <Select
// *     // other props
// *     {...formatters}
// *   />
// */
// export const useFormatters = () => {

//   // useful for CreatableSelect
//   const formatCreateLabel = (label) => (
//     <span className={'text-sm'}>
//       Add
//       <span className={'font-semibold'}>{` "${label}"`}</span>
//     </span>
//   );

//   // useful for GroupedOptions
//   const formatGroupLabel = (data) => (
//     <div className={'flex justify-between items-center'}>
//       <span>{data.label}</span>
//       <span
//         className={
//           'rounded-md text-xs font-normal text-secondary-foreground bg-secondary shadow-sm px-1'
//         }
//       >
//         {data.options.length}
//       </span>
//     </div>
//   );
//   return {
//     formatCreateLabel,
//     formatGroupLabel
//   };
// };
